import React, { useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  CircularProgress
} from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';
import { CollectorScope } from 'graphql/types';
import { useCollectorSetting } from '../hooks';
import { useUpdateSettingPreferencesForCollectorSetting } from '../hooks/useUpdateSettingPreferencesForCollectorSetting';
import { CollectorInstallInfo } from '../CollectorsListPage';
import { useSettingMetaData } from './hooks/useSettingMetaData';
import { SettingLink } from './SettingLink';
import { DropDownList } from './DropDownList';
import { CollectorErrorList } from './CollectorErrorList';

type SettingDialogProps = {
  onClose: () => void;
  open: boolean;
  scope: CollectorScope;
  collectorInstallInfo: CollectorInstallInfo;
};

const useStyles = makeStyles((theme) => ({
  actions: {
    justifyContent: 'flex-start',
    padding: theme.spacing(0, 4, 1, 2)
  },
  loading: {
    display: 'flex',
    justifyContent: 'center'
  }
}));

export const SettingDialog: React.FC<SettingDialogProps> = ({
  onClose,
  open,
  scope,
  collectorInstallInfo
}: SettingDialogProps) => {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const classes = useStyles();
  const { register, setValue, handleSubmit } = useForm();
  const input = { installId: collectorInstallInfo.installId };
  const { data, refetch, loading } = useCollectorSetting(input);
  const setting = data?.collectorSetting?.item?.settings;

  const errors = data?.collectorSetting?.item?.installStatus?.errors;

  useEffect(() => {
    if (!collectorInstallInfo) return;
    if (!setting) return;
    setting?.forEach((index) => {
      setValue(index!.key, index?.value);
    });
  }, [setValue, setting, collectorInstallInfo]);

  const settings = useSettingMetaData(collectorInstallInfo.collectorItem.name);
  const {
    updateSettingPreferences
  } = useUpdateSettingPreferencesForCollectorSetting();

  const onSubmit = useCallback(
    (data: any) => {
      const saveStatus = async () => {
        await updateSettingPreferences({
          installId: collectorInstallInfo.installId,
          settings: [
            { key: 'collectorEnabled', value: data.collectorEnabled },
            {
              key: 'allowUsersToOptOutEnabled',
              value: data.allowUsersToOptOutEnabled
            }
          ]
        });
        await refetch({
          variables: {
            installId: collectorInstallInfo.installId
          }
        });
      };
      saveStatus();
      onClose();
    },
    [onClose, updateSettingPreferences, collectorInstallInfo.installId, refetch]
  );
  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      open={open}
      fullWidth={true}
      maxWidth="md"
      data-testid="tenant-setting-dialog"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          <FormattedMessage
            id="collectorSetting.title"
            values={{ prefix: collectorInstallInfo.collectorItem.name }}
          />
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <div className={classes.loading}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <DropDownList settingMetadata={settings} register={register} />
              {errors && errors.length > 0 && (
                <CollectorErrorList
                  installId={collectorInstallInfo.installId}
                  errors={errors}
                  collectorId={collectorInstallInfo?.collectorItem?.id}
                  retryURL={collectorInstallInfo?.collectorItem?.retryURL}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions className={classes.actions}>
          <SettingLink
            collectorItem={collectorInstallInfo.collectorItem}
            onClose={onClose}
            scope={scope}
          />
          <Button onClick={handleClose} data-testid="cancel-button">
            <FormattedMessage id="collectorSetting.cancel" />
          </Button>
          <Button color="primary" variant="contained" type="submit">
            <FormattedMessage id="collectorSetting.save" />
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
